import { StringMap } from 'i18next';
import moment from 'moment';

import { Partner } from 'src/reducers/partner/types';

import { convertMgToMmol } from 'Utilities/dataHelpers';
import { _config } from 'Utilities/envConfig';
import i18n from 'Utilities/i18n';
import log from 'Utilities/log';
import mediator from 'Utilities/mediator';
import { getSessionObject } from 'Utilities/storage';
import { observeStore } from 'Utilities/store';

import { DateFormats, TimeFormats } from 'Constants/formatTypes';
import { PARTNER_TYPES } from 'Constants/partnerTypes';

let currentDateFormats = DateFormats['1'];
let currentTimeFormats = TimeFormats['1'];

const changedDateFormat = (format: string) => {
  currentDateFormats = DateFormats[format || '1'];
};

const changedTimeFormat = (format: string) => {
  currentTimeFormats = TimeFormats[format || '1'];
};

/**
 * pass in an object with day, month, and year to get back a unix timestamp
 * day is optional
 */
export const createDateString = ({ day, month, year }: { [key: string]: number | undefined }) => {
  if (month === undefined || year === undefined) {
    log.panic('createTimestamp: must pass in year and month');
    return '';
  }

  if (day === undefined) {
    return moment(`${year}-${month}-01`, 'YYYY-MM-DD').format('YYYY-MM-DD');
  } else {
    return moment(`${year}-${month}-${day}`, 'YYYY-MM-DD').format('YYYY-MM-DD');
  }
};

export const createDateStringFromJsDate = (date: Date) => {
  return moment(date).format('YYYY-MM-DD');
};

export const createUnixFromJsDate = (date = new Date()) => {
  return moment(date).unix();
};

export const findHcpinPractices = (id: string, practices: any[] | any) => {
  if (!practices) {
    practices = getSessionObject('practices');
  }

  if (!Array.isArray(practices)) {
    practices = Object.values(practices);
  }

  for (const practice of practices) {
    const hcp = practice.careTeam.find((c: any) => c.id === id);

    if (hcp) {
      return hcp;
    }
  }

  return null;
};

export const timestampNow = () => moment().unix();

export const getCurrentUser = () => {
  return getSessionObject('userData');
};

export const mgToMmolIfNeeded = (value: string | number, uom: string) => {
  if (uom === '1') {
    return value;
  }

  return convertMgToMmol(value);
};

export const sortByTimestamp = <T extends { timestamp: number }>(a: T, b: T) => {
  const aDate = a.timestamp;
  const bDate = b.timestamp;

  return bDate - aDate;
};

// This assumes that we will NEVER use milliseconds for timestamps
export const _isUnixTime = (value: any) => {
  // need these checks because the number check passes when passed in a jsDate or moment object
  if (value && (value._isAMomentObject || value.toLocaleTimeString)) {
    return false;
  }

  return Number.isInteger(Number(value)); // null computes to true here, undefined to false
};

export const _getMoment = (value: any) => (_isUnixTime(value) ? moment.unix(value) : moment(value));

export const getUnixTimstamp = (value = moment()) => _getMoment(value).unix();

/**
 * Generates a format based on user settings to be used for moment formatting
 *
 * @param {string} format Either a key corresponding to a custom date format
 * @param {string} [time] A time format option to add to the end
 * @param {string} [seperator] The seperator to use between date and time, otherwise a space is used
 */
export const makeFormatString = (format: string, time = '', seperator = '') => {
  let result = '';

  if (currentDateFormats[format]) {
    result += currentDateFormats[format];
  } else if (format) {
    return format;
  }

  if (currentTimeFormats[time]) {
    result += result.length > 0 ? (seperator ? ` ${seperator} ` : ' ') : '';
    result += currentTimeFormats[time];
  }

  return result;
};

/**
 *
 * @param group Translation group, defines the top level key in the translation file
 * @param partnerKey If there are specific translation texts for partner types that differ grom the general text we have we add them with the partner type as the key
 * @param key They key for the translation
 * @param opts optional params for the translation for variable text
 * @returns the translated string for the partner type if it exits or defaults back to the normal string
 */

export const getPartnerTranslation = (
  group: string,
  partnerKey: string,
  key: string,
  opts?: StringMap
): string => {
  let tKey: string = group + '.' + key;

  partnerKey = capitalizeFirstChar(partnerKey);

  if (i18n.exists(group + '.' + partnerKey + '.' + key)) {
    tKey = group + '.' + partnerKey + '.' + key;
  }

  if (opts && typeof opts !== undefined) {
    return i18n.t(tKey, opts);
  }

  return i18n.t(tKey);
};

/**
 *
 * @param group Translation group, defines the top level key in the translation file
 * @param partnerKey If there are specific translation texts for partner types that differ grom the general text we have we add them with the partner type as the key
 * @param key They key for the translation
 * @returns the key for partner ovveride text if exists or defaults back to the normal key
 */

export const getPartnerTranslationKey = (
  group: string,
  partnerKey: string,
  key: string
): string => {
  let tKey: string = group + '.' + key;

  partnerKey = capitalizeFirstChar(partnerKey);

  if (i18n.exists(group + '.' + partnerKey + '.' + key)) {
    tKey = group + '.' + partnerKey + '.' + key;
  }

  return tKey;
};

const capitalizeFirstChar = (key: string): string => {
  if (key !== '' && key.length > 1) {
    return key[0].toUpperCase() + key.slice(1).toLowerCase();
  }

  return key;
};

/**
 * Formats a timestamp to the specified format type
 *
 * @param {moment.MomentInput} value The timestamp to use
 * @param {string} format A date format type or compatible moment format string
 * @param {string} [time] A time format option to add to the end
 * @param {string} [seperator] The seperator to use between date and time, otherwise a space is used
 */
export const timeStampFormat = (value: any, format: string, time?: string, seperator?: string) =>
  _getMoment(value).format(makeFormatString(format, time, seperator));

// Since we are calling this in one place we are just rogue casting moment instead of adding the function as a typing
const _createFromInputFallback = (moment as any).createFromInputFallback;

(moment as any).createFromInputFallback = (config: any) => {
  if (!_config.production) {
    log.error('Detected a momentjs fallback to new Date()!');
    alert('Detected a momentjs fallback to new Date()!');
  }

  _createFromInputFallback(config);
};

/**
 * Determines whether to display the partner when there is no current connection
 *
 * @param {Partner} value The partner object. Some partner types don't have a display property so we handle that here.
 *
 */
export const displayPartner = (partner: Partner): boolean => {
  switch (partner.type) {
    case PARTNER_TYPES.API:
    case PARTNER_TYPES.INBOUND:
      return partner.display ?? false;
    default:
      return false;
  }
};

// Setup observers
mediator.once('store:created', () => {
  observeStore((state) => state.user.dateFormat, changedDateFormat);
  observeStore((state) => state.user.timeFormat, changedTimeFormat);
});
